import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { Compass, Bookmark, Gift, Timer, Infinity as InfinityIcon, MessagesSquare, Images, ChevronRight } from "lucide-react";

// Small "Entdecken" discovery strip surfacing the newer client extras
// (Collections, Method-Player via Unlocked, Focus-Timer, Connected-Breath,
// Collab area, Story-Wall). Reused in the Breathwork hub and the Feed hub.
//
// Two live nudge styles:
//  - badge:"new"    → data-backed chips show "Neu +N" when their count grew
//                     since the user last opened them (seen-counts in localStorage,
//                     cleared on visit).
//  - badge:"unread" → mirrors the sidebar: a live unread-count badge
//                     (e.g. unread collab chats). Not seen-tracked; it clears
//                     server-side once the chats are read.
const SEEN_KEY = "mw_discover_seen";
const ITEMS = [
  { to: "/app/collections", key: "collections", icon: Bookmark, endpoint: "/bookmarks/folders", extract: (d) => (Array.isArray(d) ? d.length : 0), badge: "new" },
  { to: "/app/unlocked-methods", key: "unlocked_methods", icon: Gift, endpoint: "/methods/my-unlocked", extract: (d) => (Array.isArray(d) ? d.length : 0), badge: "new" },
  { to: "/app/focus-timer", key: "focus_timer", icon: Timer },
  { to: "/app/connected-breath", key: "connected_breath", icon: InfinityIcon },
  { to: "/app/collab", key: "collab_area", icon: MessagesSquare, endpoint: "/client/collab/chats", extract: (d) => (d?.chats || []).reduce((sum, c) => sum + (c.unread_count || 0), 0), badge: "unread" },
  { to: "/app/story-wall", key: "story_wall", icon: Images },
];

function readSeen() {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY) || "{}") || {}; } catch { return {}; }
}
function writeSeen(next) {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify(next)); } catch { /* ignore */ }
}

export default function DiscoverStrip() {
  const t = useT();
  const [counts, setCounts] = useState({});
  const [seen, setSeen] = useState(readSeen);

  useEffect(() => {
    let alive = true;
    Promise.all(
      ITEMS.filter((i) => i.endpoint).map((i) =>
        api.get(i.endpoint).then(({ data }) => [i.key, i.extract(data)]).catch(() => [i.key, 0])
      )
    ).then((pairs) => { if (alive) setCounts(Object.fromEntries(pairs)); });
    return () => { alive = false; };
  }, []);

  const markSeen = (key) => {
    if (!(key in counts)) return;
    const next = { ...readSeen(), [key]: counts[key] };
    writeSeen(next);
    setSeen(next);
  };

  return (
    <section data-testid="discover-strip" className="rounded-2xl border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10"><Compass className="h-4 w-4 text-primary" /></span>
        <div className="min-w-0">
          <p className="font-display text-sm text-foreground">{t("discover_strip_title")}</p>
          <p className="truncate text-xs text-muted-foreground">{t("discover_strip_sub")}</p>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {ITEMS.map((it) => {
          const count = it.key in counts ? counts[it.key] : 0;
          const delta = it.badge === "new" ? Math.max(0, count - (seen[it.key] || 0)) : 0;
          const unread = it.badge === "unread" ? count : 0;
          return (
            <Link key={it.key} to={it.to} onClick={() => it.badge === "new" && markSeen(it.key)} data-testid={`discover-strip-item-${it.key}`}
              className="group flex shrink-0 items-center gap-2 rounded-full border border-border bg-secondary/50 px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-primary/10">
              <it.icon className="h-4 w-4 shrink-0 text-primary" />
              <span className="whitespace-nowrap">{t(`nav.${it.key}`)}</span>
              {delta > 0 ? (
                <span data-testid={`discover-strip-new-${it.key}`} className="inline-flex shrink-0 items-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
                  {t("discover_strip_new")} +{delta}
                </span>
              ) : unread > 0 ? (
                <span data-testid={`discover-strip-unread-${it.key}`} className="inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-semibold text-white">
                  {unread > 99 ? "99+" : unread}
                </span>
              ) : (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              )}
            </Link>
          );
        })}
      </div>
    </section>
  );
}
